import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
    Database,
    HeartPulse,
    Loader2,
    Lock,
    Network,
    RotateCcw,
    Save,
    Undo2,
    Users,
    WifiOff,
    Wrench,
    type LucideIcon
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useMobilePolicy, useUpdateMobilePolicy } from "@/api/hooks";
import { AdminApiError, type AttestationMode, type PinningMode, type PlatformVersions, type ShareRule } from "@/api/adminBff";
import {
    ANDROID_API_LABELS,
    ATTESTATION_MODES,
    DEFAULT_MOBILE_POLICY,
    NUMERIC_LIMITS,
    PINNING_MODES,
    SHARE_RULES,
    fromDraft,
    toDraft,
    validateDraft,
    withDefaults,
    type NumericKey,
    type PolicyDraft
} from "./mobilePolicy";
import type { Notify } from "./Toast";

const selectCls =
    "w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary";
const textareaCls =
    "w-full rounded-md border bg-background px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary";

type BoolKey = { [K in keyof PolicyDraft]: PolicyDraft[K] extends boolean ? K : never }[keyof PolicyDraft];

/**
 * Enforcement status of a setting in the current app builds (Mobile/docs/phase0-contract.md §9A):
 * `later` = saved, signed and delivered, but not acted on yet; `partial` = only partly acted on.
 */
type Rollout = "later" | "partial";

/** A field-level error from Identity / the BFF: 400 `{ error, field }`. */
interface ServerFieldError {
    field: string;
    message: string;
}

/** The DOM id of the input a server `field` refers to (`minOsVersion.ios` → `mp-minOsVersion-ios`). */
function fieldElementId(field: string): string {
    if (field === "minOsVersion" || field === "minAppVersion") return `mp-${field}-ios`;
    return `mp-${field.replace(".", "-")}`;
}

/**
 * App protection policy editor (contract §5). Identity signs the saved policy (ES256), bumps its
 * version and devices pick it up on their next check-in.
 */
export function MobilePolicyTab({ tenantId, tenantName, notify }: { tenantId: string; tenantName: string; notify: Notify }) {
    const policy = useMobilePolicy(tenantId);
    const update = useUpdateMobilePolicy(tenantId);

    const baseline = useMemo(
        () => (policy.data ? toDraft(withDefaults(policy.data.policy)) : null),
        [policy.data]
    );
    const [draft, setDraft] = useState<PolicyDraft | null>(null);
    const [serverError, setServerError] = useState<ServerFieldError | null>(null);
    useEffect(() => {
        setDraft(baseline);
        setServerError(null);
    }, [baseline]);
    const errors = useMemo(() => (draft ? validateDraft(draft) : {}), [draft]);

    if (policy.isError) {
        return (
            <div className="rounded-lg border border-destructive/40 bg-card p-4 text-sm">
                <p className="font-medium text-destructive">Couldn&apos;t load the app protection policy.</p>
                <p className="mt-1 text-muted-foreground">{policy.error.message}</p>
                <Button variant="outline" size="sm" className="mt-3" onClick={() => void policy.refetch()}>
                    Try again
                </Button>
            </div>
        );
    }
    if (policy.isPending || !draft || !baseline) {
        return (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading policy…
            </div>
        );
    }

    const env = policy.data;
    const errorCount = Object.keys(errors).length;
    const dirty = JSON.stringify(draft) !== JSON.stringify(baseline);
    const isDefault = JSON.stringify(draft) === JSON.stringify(toDraft(DEFAULT_MOBILE_POLICY));

    // Client-side errors, plus the server's rejection shown on the field it names (until the next edit).
    const shown: Record<string, string> = { ...errors };
    if (serverError && !shown[serverError.field]) shown[serverError.field] = serverError.message;

    const set = <K extends keyof PolicyDraft>(key: K, value: PolicyDraft[K]) => {
        setServerError(null);
        setDraft((d) => (d ? { ...d, [key]: value } : d));
    };
    const setVersion = (key: "minOsVersion" | "minAppVersion", platform: keyof PlatformVersions, value: string) => {
        setServerError(null);
        setDraft((d) => (d ? { ...d, [key]: { ...d[key], [platform]: value } } : d));
    };

    const save = () => {
        if (errorCount > 0) return;
        setServerError(null);
        update.mutate(fromDraft(draft), {
            onSuccess: (saved) =>
                notify(
                    "success",
                    `Policy saved for ${tenantName || "this tenant"}` +
                        (saved?.version != null ? ` — now version ${saved.version}` : "") +
                        (saved?.etag ? ` (ETag ${saved.etag})` : "") +
                        ". Devices pick it up at their next check-in, normally within 15 minutes."
                ),
            onError: (e) => {
                if (e instanceof AdminApiError && e.field) {
                    setServerError({ field: e.field, message: e.message });
                    const el = document.getElementById(fieldElementId(e.field));
                    if (el) {
                        el.scrollIntoView({ block: "center", behavior: "smooth" });
                        el.focus({ preventScroll: true });
                    }
                }
                notify("error", `Couldn't save the policy: ${e.message}`);
            }
        });
    };

    const toggleRow = (key: BoolKey, label: string, help: ReactNode, rollout?: Rollout) => (
        <div className="flex items-start gap-3 px-5 py-3">
            <input
                type="checkbox"
                id={`mp-${key}`}
                checked={draft[key]}
                onChange={(e) => set(key, e.target.checked)}
                aria-invalid={Boolean(shown[key])}
                className="mt-0.5 h-4 w-4"
            />
            <div className="flex-1">
                <label htmlFor={`mp-${key}`} className="text-sm font-medium">{label}</label>
                {rollout ? <RolloutBadge rollout={rollout} /> : null}
                <p className="text-xs text-muted-foreground">{help}</p>
                {shown[key] ? <p className="mt-1 text-xs text-destructive" role="alert">{shown[key]}</p> : null}
            </div>
        </div>
    );

    const numberRow = (key: NumericKey, label: string, help: ReactNode, rollout?: Rollout) => (
        <FieldRow id={`mp-${key}`} label={label} help={help} error={shown[key]} rollout={rollout}>
            <div className="flex items-center gap-2">
                <Input
                    id={`mp-${key}`}
                    type="number"
                    inputMode="numeric"
                    min={NUMERIC_LIMITS[key].min}
                    max={NUMERIC_LIMITS[key].max}
                    value={draft[key]}
                    onChange={(e) => set(key, e.target.value)}
                    aria-invalid={Boolean(shown[key])}
                    className="w-28"
                />
                <span className="text-xs text-muted-foreground">{NUMERIC_LIMITS[key].unit}</span>
            </div>
        </FieldRow>
    );

    const versionRow = (key: "minOsVersion" | "minAppVersion", label: string, help: ReactNode, hint?: string) => {
        const error = [shown[key], shown[`${key}.ios`], shown[`${key}.android`]].filter(Boolean).join(" ");
        return (
            <FieldRow id={`mp-${key}-ios`} label={label} help={help} error={error || undefined}>
                <div className="grid grid-cols-2 gap-2">
                    <label className="flex flex-col gap-1 text-xs">
                        <span className="text-muted-foreground">iOS</span>
                        <Input
                            id={`mp-${key}-ios`}
                            value={draft[key].ios}
                            onChange={(e) => setVersion(key, "ios", e.target.value)}
                            placeholder={DEFAULT_MOBILE_POLICY[key].ios}
                            aria-invalid={Boolean(shown[`${key}.ios`] || shown[key])}
                        />
                    </label>
                    <label className="flex flex-col gap-1 text-xs">
                        <span className="text-muted-foreground">
                            Android{key === "minOsVersion" ? " (API level)" : ""}
                        </span>
                        <Input
                            id={`mp-${key}-android`}
                            value={draft[key].android}
                            onChange={(e) => setVersion(key, "android", e.target.value)}
                            placeholder={DEFAULT_MOBILE_POLICY[key].android}
                            aria-invalid={Boolean(shown[`${key}.android`] || shown[key])}
                        />
                    </label>
                </div>
                {hint ? <p className="mt-1 text-xs text-muted-foreground">{hint}</p> : null}
            </FieldRow>
        );
    };

    const apiLevel = Number(draft.minOsVersion.android);
    const androidHint = ANDROID_API_LABELS[apiLevel] ? `API ${apiLevel} = ${ANDROID_API_LABELS[apiLevel]}` : undefined;

    return (
        <div className="space-y-4">
            <section className="rounded-lg border bg-card p-4 shadow-sm">
                <div className="flex flex-wrap items-center gap-2">
                    <h2 className="font-semibold">App protection policy{tenantName ? ` — ${tenantName}` : ""}</h2>
                    <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                        Version {env?.version ?? "—"}
                    </span>
                    {env?.etag ? <span className="font-mono text-xs text-muted-foreground">ETag {env.etag}</span> : null}
                </div>
                {env?.updatedAt ? (
                    <p className="mt-1 text-xs text-muted-foreground">
                        Last changed {new Date(env.updatedAt).toLocaleString()}
                        {env.updatedBy ? ` by ${env.updatedBy}` : ""}
                    </p>
                ) : null}
                <p className="mt-2 text-sm text-muted-foreground">
                    Applies to Athavita Mail, Connect and Authenticator for everyone in this tenant. The policy is signed
                    by the Identity service and each app downloads it when it checks in. Revoke and wipe commands are
                    carried out by the server; the other settings are enforced inside the apps, and each one says what
                    it can and can&apos;t guarantee. Settings marked <RolloutBadge rollout="later" inline /> are saved,
                    signed and delivered to phones now, but the current app versions don&apos;t act on them yet. A
                    customer&apos;s MDM can make these settings stricter but never looser (see{" "}
                    <em>Managed configuration</em>).
                </p>
            </section>

            <Section icon={Lock} title="Access">
                {toggleRow(
                    "requireAppPin",
                    "Require an app lock",
                    "Users must unlock the Athavita app with an Athavita PIN before any content is shown — when it starts and after the time set below. Face ID / fingerprint can stand in for the PIN when allowed below."
                )}
                {toggleRow(
                    "allowBiometric",
                    "Allow Face ID / fingerprint to unlock",
                    "When on, Face ID / fingerprint can unlock the app instead of the Athavita PIN. When off, only the PIN unlocks it. Changing or removing the PIN always needs the current PIN — biometrics are never enough for that."
                )}
                {numberRow(
                    "pinLength",
                    "Minimum PIN length",
                    "Shortest Athavita PIN users can choose when they set or change their PIN."
                )}
                {numberRow(
                    "pinRetryLimit",
                    "Wrong PIN attempts before wipe",
                    "After this many wrong Athavita PINs in a row, the app deletes its Athavita data from the phone and signs out (when 'Wipe on failure' is on). A personal Authenticator vault with no work account is never wiped — it waits longer between attempts instead."
                )}
                {numberRow(
                    "reAuthAfterMinutes",
                    "Lock again after",
                    "When the app hasn't been used for this long, it locks and asks the user to unlock it again."
                )}
            </Section>

            <Section icon={HeartPulse} title="Device health">
                {toggleRow(
                    "blockJailbrokenRooted",
                    "Block jailbroken / rooted phones",
                    "Refuse to open on phones where the operating system's protections have been removed — and, with 'Wipe on failure' on, delete the Athavita data on them. Detection is best-effort — a determined user can hide root — so combine it with attestation."
                )}
                <FieldRow
                    id="mp-attestationMode"
                    label="Device attestation"
                    help="Asks Apple (App Attest) or Google (Android key attestation) to vouch that this is the genuine Athavita app on real hardware. 'Warn' records the result in the device list but lets everyone in. 'Block' refuses every device that isn't verified — including phones where attestation isn't available, such as de-Googled or custom-ROM phones. Block refuses; it never wipes."
                    error={shown.attestationMode}
                >
                    <select
                        id="mp-attestationMode"
                        value={draft.attestationMode}
                        onChange={(e) => set("attestationMode", e.target.value as AttestationMode)}
                        aria-invalid={Boolean(shown.attestationMode)}
                        className={selectCls}
                    >
                        {ATTESTATION_MODES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                </FieldRow>
                {versionRow(
                    "minOsVersion",
                    "Minimum OS version",
                    "Phones below this version see an 'update required' screen. The apps themselves need iOS 17 or Android 10 (API 29) at least.",
                    androidHint
                )}
                {versionRow(
                    "minAppVersion",
                    "Minimum app version",
                    "Older builds of the Athavita apps must be updated from the App Store / Google Play before they can be used."
                )}
            </Section>

            <Section icon={Database} title="Data protection">
                <FieldRow
                    id="mp-blockClipboardOut"
                    label="Copying text out of the apps"
                    help="'Only into other Athavita apps' lets users paste company content into Athavita apps but not into WhatsApp, Notes, etc. Authenticator codes can always be copied (the clipboard is cleared after 30 seconds)."
                    error={shown.blockClipboardOut}
                    rollout="later"
                >
                    <select
                        id="mp-blockClipboardOut"
                        value={draft.blockClipboardOut}
                        onChange={(e) => set("blockClipboardOut", e.target.value as ShareRule)}
                        aria-invalid={Boolean(shown.blockClipboardOut)}
                        className={selectCls}
                    >
                        {SHARE_RULES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                </FieldRow>
                <FieldRow
                    id="mp-blockShareOut"
                    label="Sharing through the share sheet"
                    help="Controls which apps appear when a user taps Share on a message, file or link."
                    error={shown.blockShareOut}
                    rollout="later"
                >
                    <select
                        id="mp-blockShareOut"
                        value={draft.blockShareOut}
                        onChange={(e) => set("blockShareOut", e.target.value as ShareRule)}
                        aria-invalid={Boolean(shown.blockShareOut)}
                        className={selectCls}
                    >
                        {SHARE_RULES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                </FieldRow>
                {toggleRow(
                    "blockSaveToLocal",
                    "Block saving to the phone",
                    "Hides 'Save to Files / Photos' so attachments can't be saved to the phone or a personal cloud drive.",
                    "later"
                )}
                {toggleRow(
                    "blockScreenshot",
                    "Block screenshots",
                    "Android: screenshots and screen recording are blocked and the app is hidden in Recents. iPhone can't block screenshots — the app hides its content while the screen is recorded or mirrored, blanks the app-switcher preview and logs screenshots, but a screenshot still gets taken."
                )}
                {toggleRow(
                    "blockBackup",
                    "Exclude from phone backups",
                    "Athavita data is kept out of iCloud and Google backups, so it isn't restored onto another phone."
                )}
                {toggleRow(
                    "allowOpenInUnmanaged",
                    "Allow opening attachments in other apps",
                    "When off, attachments open only in Athavita's built-in viewers, never in a personal PDF reader or office app.",
                    "later"
                )}
                {toggleRow(
                    "requireManagedBrowser",
                    "Open links in the in-app browser",
                    "Links open in the app's own browser, under this same policy, instead of Safari or Chrome.",
                    "later"
                )}
            </Section>

            <Section icon={WifiOff} title="Offline access">
                {numberRow(
                    "offlineGraceHours",
                    "Offline grace period",
                    "How long the apps keep working without checking in with Athavita. After this they lock — they don't wipe — until they can check in and refresh the policy."
                )}
                {numberRow(
                    "maxOfflineDays",
                    "Maximum days offline",
                    "The offline cache window: how many days of mail and chat the apps may keep on the phone for offline use. It doesn't lock, sign out or wipe anything — the grace period above is what locks an app that can't check in."
                )}
                {toggleRow(
                    "wipeOnPolicyFail",
                    "Wipe on failure",
                    "When on, the app deletes its Athavita data instead of just refusing to open in two cases: 'Block jailbroken / rooted phones' is on and the app detects a jailbroken or rooted phone, or the wrong-PIN limit above is reached. No other failed check wipes a phone — attestation in Block mode, an OS or app below the minimum, or a passed offline grace period only lock the app or ask for an update."
                )}
            </Section>

            <Section icon={Network} title="Network">
                <FieldRow
                    id="mp-networkAllowlistCidrs"
                    label="Allowed networks"
                    wide
                    help="The IP ranges the mobile apps may reach Athavita from (for example your office or VPN egress). This list is checked by the server, not the phone — but the server doesn't enforce it yet; it's saved and signed now so it's ready when enforcement ships. CIDR notation, one per line (a single address is written 203.0.113.7/32); leave empty for no restriction. Phones on mobile data will not match an office range."
                    error={shown.networkAllowlistCidrs}
                    rollout="later"
                >
                    <textarea
                        id="mp-networkAllowlistCidrs"
                        rows={3}
                        value={draft.networkAllowlistCidrs}
                        onChange={(e) => set("networkAllowlistCidrs", e.target.value)}
                        placeholder={"203.0.113.0/24\n2001:db8::/48"}
                        aria-invalid={Boolean(shown.networkAllowlistCidrs)}
                        className={textareaCls}
                    />
                </FieldRow>
            </Section>

            <Section icon={Users} title="Accounts">
                <FieldRow
                    id="mp-allowedAccounts"
                    label="Allowed accounts"
                    wide
                    help="Only these accounts can sign in to the apps. One email address or @domain per line; leave empty to allow any account in this tenant. If the MDM also sets a list, only accounts on both lists are allowed."
                    error={shown.allowedAccounts}
                >
                    <textarea
                        id="mp-allowedAccounts"
                        rows={3}
                        value={draft.allowedAccounts}
                        onChange={(e) => set("allowedAccounts", e.target.value)}
                        placeholder={"@example.com\nalice@example.com"}
                        aria-invalid={Boolean(shown.allowedAccounts)}
                        className={textareaCls}
                    />
                </FieldRow>
            </Section>

            <Section icon={Wrench} title="Advanced">
                <FieldRow
                    id="mp-pinningMode"
                    label="Certificate pinning"
                    help="The apps only trust Athavita's own server certificates. 'Report only' logs mismatches without blocking; 'Enforce' refuses the connection — which breaks the apps behind corporate proxies that inspect TLS traffic. Leave Off unless you need it."
                    error={shown.pinningMode}
                    rollout="later"
                >
                    <select
                        id="mp-pinningMode"
                        value={draft.pinningMode}
                        onChange={(e) => set("pinningMode", e.target.value as PinningMode)}
                        aria-invalid={Boolean(shown.pinningMode)}
                        className={selectCls}
                    >
                        {PINNING_MODES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                </FieldRow>
            </Section>

            <div className="sticky bottom-0 z-10 flex flex-wrap items-center gap-2 rounded-lg border bg-background/95 px-4 py-3 shadow-md backdrop-blur">
                <span
                    className={`text-xs ${errorCount > 0 || serverError ? "text-destructive" : "text-muted-foreground"}`}
                    role={serverError ? "alert" : undefined}
                >
                    {errorCount > 0
                        ? `${errorCount} ${errorCount === 1 ? "setting needs" : "settings need"} attention`
                        : serverError
                          ? `Not saved — ${serverError.message}`
                          : dirty
                            ? "Unsaved changes — nothing reaches devices until you save."
                            : "No unsaved changes."}
                </span>
                <Button
                    variant="ghost"
                    size="sm"
                    className="ml-auto"
                    onClick={() => {
                        setServerError(null);
                        setDraft(toDraft(DEFAULT_MOBILE_POLICY));
                    }}
                    disabled={isDefault || update.isPending}
                    title="Fill in the recommended defaults. Nothing changes until you save."
                >
                    <RotateCcw className="h-3.5 w-3.5" /> Reset to defaults
                </Button>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                        setServerError(null);
                        setDraft(baseline);
                    }}
                    disabled={!dirty || update.isPending}
                >
                    <Undo2 className="h-3.5 w-3.5" /> Discard changes
                </Button>
                <Button size="sm" onClick={save} disabled={!dirty || errorCount > 0 || update.isPending}>
                    {update.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                    Save policy
                </Button>
            </div>
        </div>
    );
}

/** Small, honest marker for settings the current app builds don't (fully) enforce yet — never hidden. */
function RolloutBadge({ rollout, inline }: { rollout: Rollout; inline?: boolean }) {
    const later = rollout === "later";
    return (
        <span
            className={`${inline ? "" : "ml-2 "}inline-flex items-center whitespace-nowrap rounded-full border border-amber-300 bg-amber-50 px-1.5 py-px align-middle text-[10px] font-medium text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200`}
            title={
                later
                    ? "Saved, signed and delivered to phones now, but the current app versions don't act on this setting yet."
                    : "Only partly enforced by the current app versions — the description says which part."
            }
        >
            {later ? "Applies from a later release" : "Partly applies today"}
        </span>
    );
}

function Section({ icon: Icon, title, children }: { icon: LucideIcon; title: string; children: ReactNode }) {
    return (
        <section className="rounded-lg border bg-card shadow-sm">
            <header className="flex items-center gap-2 border-b px-5 py-3">
                <Icon className="h-4 w-4 text-muted-foreground" />
                <h3 className="font-semibold">{title}</h3>
            </header>
            <div className="divide-y">{children}</div>
        </section>
    );
}

function FieldRow({
    id,
    label,
    help,
    error,
    wide,
    rollout,
    children
}: {
    id: string;
    label: string;
    help: ReactNode;
    error?: string | undefined;
    wide?: boolean;
    rollout?: Rollout | undefined;
    children: ReactNode;
}) {
    return (
        <div className={`grid gap-2 px-5 py-3 ${wide ? "" : "sm:grid-cols-[minmax(0,1fr)_18rem] sm:items-start sm:gap-6"}`}>
            <div>
                <label htmlFor={id} className="text-sm font-medium">{label}</label>
                {rollout ? <RolloutBadge rollout={rollout} /> : null}
                <p className="text-xs text-muted-foreground">{help}</p>
            </div>
            <div>
                {children}
                {error ? <p className="mt-1 text-xs text-destructive" role="alert">{error}</p> : null}
            </div>
        </div>
    );
}
